import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, User, Terminal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, apiError } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import AdminDashboard from "@/pages/AdminDashboard";

export default function Access() {
  const { admin, ready, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!ready)
    return <div className="flex min-h-screen items-center justify-center bg-[#08090E] font-mono text-emerald-400">authenticating…</div>;

  if (admin) return <AdminDashboard />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username, password);
      toast.success("Access granted");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="co-grid flex min-h-screen items-center justify-center bg-[#08090E] p-4">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", damping: 24, stiffness: 260 }}
        className="co-scanline relative w-full max-w-sm overflow-hidden rounded-2xl border border-[#1E2433] bg-[#10131B] p-7"
      >
        <div className="mb-6 text-center">
          <div className="mx-auto flex w-fit">
            <LogoMark size="lg" />
          </div>
          <h1 className="font-display mt-3 text-2xl font-extrabold text-slate-100">
            crazy<span className="text-emerald-400">One</span> Portal
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">restricted // authorized access only</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Username</label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className="w-full rounded-lg border border-[#1E2433] bg-[#0b0d13] py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition-colors focus:border-emerald-500/60"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Password</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="w-full rounded-lg border border-[#1E2433] bg-[#0b0d13] py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition-colors focus:border-emerald-500/60"
              />
            </div>
          </div>
          <button data-testid="login-submit" type="submit" disabled={busy} className="co-btn-primary flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {busy ? "Verifying…" : "Enter portal"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
